import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Close20 from '@carbon/icons-react/es/close/20';
import { QueryClientProvider } from '@tanstack/react-query';
import { notification, Space } from 'antd';
import { Provider } from 'react-redux';
import styled from 'styled-components';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { ProductiveHeading07, Text02, Text01, BodyShort01, ColFlexBox01 } from 'components/styles';
import { store } from 'data';
import { queryClient } from 'queries';
import { CheckmarkIcon } from './checmark';
import './dark-toast.css';
import { ErrorIcon } from './error';
import { InformationIcon } from './information';
import { WarningIcon } from './warning';
const ToastTitle = styled.div`
  ${ProductiveHeading07};
  ${Text01};
`;
const ToastDesc = styled.div`
  ${BodyShort01};
  ${ColFlexBox01};
  ${Text02};
  color: ${({ type })=>type === 'ai' || type === 'dark' ? 'var(--white-100)' : 'inherit'};
`;
export const DARK_TOAST_CLASS_NAME = 'toast-dark-compact';
// TODO: Change API
export const toastService = ({ toastContainer, duration, title, message, placement = 'bottomLeft', key })=>{
    const config = (type)=>({
            message: /*#__PURE__*/ _jsx(ToastTitle, {
                "data-cy": "toast.title",
                children: title
            }),
            description: /*#__PURE__*/ _jsx(ToastDesc, {
                "data-cy": "toast.description",
                type: type,
                children: message
            }),
            placement,
            duration,
            getContainer: ()=>{
                return (toastContainer === null || toastContainer === void 0 ? void 0 : toastContainer.current) || document.body;
            },
            closeIcon: /*#__PURE__*/ _jsx("span", {
                "data-cy": "toast.close-icon",
                children: /*#__PURE__*/ _jsx(Close20, {
                    color: type === 'ai' || type === 'dark' ? 'var(--white-100)' : 'inherit'
                })
            }),
            key
        });
    const style = {
        success: {
            backgroundColor: 'var(--green-10)',
            borderLeft: '3px solid var(--green-50)'
        },
        error: {
            backgroundColor: 'var(--red-10)',
            borderLeft: '3px solid var(--red-50)'
        },
        info: {
            backgroundColor: 'var(--blue-10)',
            borderLeft: '3px solid var(--blue-50)'
        },
        ai: {
            backgroundColor: 'var(--warm-gray-90)',
            borderRadius: 'var(--border-radius-default)',
            color: 'var(--white-100)',
            alignItems: 'center'
        },
        dark: {
            backgroundColor: 'var(--gray-90)',
            borderRadius: '4px',
            color: 'var(--white-100)'
        }
    };
    return {
        success: ()=>{
            return notification.success({
                ...config('success'),
                style: style.success,
                icon: /*#__PURE__*/ _jsx(CheckmarkIcon, {})
            });
        },
        error: ()=>{
            return notification.error({
                ...config('error'),
                style: style.error,
                icon: /*#__PURE__*/ _jsx(ErrorIcon, {})
            });
        },
        info: ()=>{
            return notification.info({
                ...config('info'),
                style: style.info,
                icon: /*#__PURE__*/ _jsx(InformationIcon, {})
            });
        },
        aiError: ()=>{
            return notification.error({
                ...config('ai'),
                style: style.ai,
                icon: /*#__PURE__*/ _jsx(WarningIcon, {})
            });
        },
        dark: ()=>{
            // used for sheets
            return notification.open({
                ...config('dark'),
                style: style.dark,
                closeIcon: false,
                className: DARK_TOAST_CLASS_NAME
            });
        }
    };
};
export const closeToast = (key)=>{
    notification.close(key);
};
/* Note:: Do not want to use brand color in button inside toast. It is decision taken by the designer.
@LinK:: https://northstar.nitro.run/#task_drawer=310.9582 */ const CTA = styled(TertiaryButton)`
  border: solid 1px var(--primary-color);
  color: var(--primary-color);
  box-shadow: var(--shadow-highlight)
    rgba(var(--blue-60-in-rgb), var(--shadow-highlight-color-opacity));

  &:focus,
  &:active,
  &:hover {
    color: var(--white-100);
    background-color: var(--primary-color) !important;
  }
`;
export const ToastDescription = ({ message = '', onClick, buttonText = '' })=>{
    return /*#__PURE__*/ _jsxs(Space, {
        direction: "vertical",
        children: [
            /*#__PURE__*/ _jsx("div", {
                "data-cy": "toast.description.custom-message-element",
                children: message
            }),
            onClick && /*#__PURE__*/ _jsx(QueryClientProvider, {
                client: queryClient,
                children: /*#__PURE__*/ _jsx(Provider, {
                    store: store,
                    children: /*#__PURE__*/ _jsx(CTA, {
                        "data-cy": "toast.action-btn",
                        size: "small",
                        onClick: onClick,
                        children: buttonText
                    })
                })
            })
        ]
    });
};
